/**
 * Newsletter browser: every filter is a picker (components/Select.jsx) or a calendar dropdown.
 *
 * Newsletters from alumni partners are left out by default, matching the partner
 * directory. The toggle brings them back, and the header says how many are being
 * held back so nothing goes missing quietly.
 *
 * Two ways to read the same rows, as on the partner directory: cards, which give the
 * opening of each update room to be read, and the table, which is the register — twelve
 * dates and word counts down a column. It opens on the cards, because an update is
 * something somebody wrote.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader, useNavCount } from '../App'
import { useApi, qs, fetchAllRows } from '../lib/api'
import { PartnerSelect, CountrySelect, EnumSelect, YearSelect } from '../components/Select'
import { FUND_TYPES } from '../../shared/fundTypes.js'
import { DateRangeField, DatePresets } from '../components/DateField'
import { PagedTable, PagedCards, ViewToggle, useViewMode } from '../components/DataTable'
import {
  Field, Badge, Empty, Filters, FilterActions, countFilters, SearchInput, fmtInt, downloadCsv,
  PartnerLabel, CountryLabel, useToast, AlumniToggle, relDays,
} from '../components/ui'
import { Icon } from '../components/Icon'

const EMPTY = {
  q: '', partner_id: [], country_id: [], partner_country_id: [],
  from: '', to: '', year: null,
  highlighted: null, has_prayer: null, source: null, country_mismatch: null,
  fund_type: null,
  include_alumni: false,
}

const ENUMS = {
  highlighted: ['0', '1'],
  has_prayer: ['0', '1'],
  country_mismatch: ['0', '1'],
  source: ['import', 'manual'],
  fund_type: FUND_TYPES,
}

/**
 * Filters can arrive in the URL, so a link from elsewhere in the app can open the
 * list already narrowed — a country on its own page, the mismatched-country button on
 * the country report. Anything unrecognised is dropped rather than passed to the API.
 */
function fromUrl(params) {
  const ids = (k) => {
    const raw = params.get(k)
    if (!raw) return []
    return raw.split(',').map((s) => Number(s)).filter((n) => Number.isInteger(n) && n > 0)
  }
  const oneOf = (k) => (ENUMS[k].includes(params.get(k)) ? params.get(k) : null)
  const year = params.get('year')
  return {
    ...EMPTY,
    q: params.get('q') ?? '',
    partner_id: ids('partner_id'),
    country_id: ids('country_id'),
    partner_country_id: ids('partner_country_id'),
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    year: year && /^\d{4}$/.test(year) ? year : null,
    highlighted: oneOf('highlighted'),
    has_prayer: oneOf('has_prayer'),
    country_mismatch: oneOf('country_mismatch'),
    source: oneOf('source'),
    fund_type: oneOf('fund_type'),
    include_alumni: params.get('include_alumni') === '1',
  }
}

export default function NewsletterList({ user }) {
  const toast = useToast()
  const [params] = useSearchParams()
  const search = params.toString()
  const [f, setF] = useState(() => fromUrl(params))
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(50)
  const [sort, setSort] = useState('published_on')
  const [dir, setDir] = useState('desc')
  const [view, setView] = useViewMode('newsletters')

  const set = (patch) => {
    setF((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  /*
   * The form owns the filters once the page is up: it is not written back to the
   * URL, so clearing a filter that arrived in a link does not fight the address
   * bar. But a second link to this same route only changes the query string and
   * leaves the page mounted, which would otherwise leave the first link's filters
   * in place — so a genuinely new query string is read again.
   */
  const applied = useRef(search)
  useEffect(() => {
    if (applied.current === search) return
    applied.current = search
    setF(fromUrl(new URLSearchParams(search)))
    setPage(1)
  }, [search])

  // The form and the API now agree on the word and on the default: alumni are out
  // unless `include_alumni=1` asks for them, which is what every other page does too.
  // Only sent when true, so it stays out of the query string in the common case.
  const { include_alumni, ...filters } = f
  const query = useApi(
    ['newsletters', f, page, limit, sort, dir],
    `newsletters${qs({ ...filters, include_alumni: include_alumni ? 1 : null, page, limit, sort, dir })}`,
    { keepPreviousData: true }
  )

  // Keep the sidebar badge on the same number as this table, filters and all.
  useNavCount('newsletters', query.data?.total)

  const onSort = (key) => {
    if (sort === key) setDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSort(key)
      setDir(key === 'published_on' ? 'desc' : 'asc')
    }
  }

  const active = countFilters(f)

  const exportAll = async () => {
    try {
      // Same alumni translation as the table's query, or the export would carry rows
      // the table is hiding.
      const all = await fetchAllRows('newsletters', {
        ...filters,
        include_alumni: include_alumni ? 1 : null,
        sort,
        dir,
      })
      downloadCsv('updates.csv', all.rows, [
        { key: 'published_on', label: 'Date' },
        { key: 's4ca_code', label: 'S4CA Code' },
        { key: 'partner_name', label: 'Partner Unit' },
        { key: 'country_name', label: 'Country' },
        { key: 'highlighted_need', label: 'Highlighted' },
        { key: 'word_count', label: 'Words' },
        { key: 'snippet', label: 'Snippet' },
      ])
      if (all.total > all.rows.length) {
        toast.info(`Exported the first ${all.rows.length} of ${all.total} rows`)
      }
    } catch (err) {
      toast.error(err)
    }
  }

  const columns = [
    {
      key: 'published_on',
      label: 'Date',
      sortKey: 'published_on',
      width: 108,
      render: (r) => (
        <Link to={`/newsletters/${r.id}`} className="mono nowrap">
          {r.published_on}
        </Link>
      ),
    },
    {
      key: 'partner',
      label: 'Partner Unit',
      sortKey: 's4ca_code',
      // The label columns keep their content on one line so the Text column can
      // claim everything they do not use. Without this the browser squeezes them
      // to their longest *word* and "S4CA-430 G and T" becomes four lines.
      cellClass: 'nowrap',
      render: (r) => <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />,
    },
    {
      key: 'country',
      label: 'Country',
      sortKey: 'country_name',
      cellClass: 'nowrap',
      render: (r) => (
        <>
          <CountryLabel id={r.country_id} name={r.country_name} />
          {r.country_mismatch ? (
            <Badge
              tone="warn"
              title={`Partner unit is registered in ${r.partner_country_name}`}
              style={{ marginLeft: 4 }}
            >
              ≠
            </Badge>
          ) : null}
        </>
      ),
    },
    {
      key: 'snippet',
      label: 'Text',
      // `grow` hands this column the space the fixed columns do not use.
      cellClass: 'cell-snippet-cell grow',
      width: '100%',
      render: (r) => <div className="cell-snippet">{r.snippet || '—'}</div>,
    },
    {
      key: 'flags',
      label: 'Flags',
      width: 96,
      render: (r) => (
        <span className="row row-tight">
          {r.highlighted_need ? <Badge tone="warn" title="Highlighted need"><Icon name="star" size={12} /></Badge> : null}
          {r.has_prayer_request ? <Badge tone="accent" title="Has prayer requests"><Icon name="prayer" size={12} /></Badge> : null}
          {r.source === 'manual' ? <Badge title="Added in the app"><Icon name="edit" size={12} /></Badge> : null}
          {r.partner_status === 'alumni' ? <Badge title="Alumni partner unit">alum</Badge> : null}
        </span>
      ),
    },
    { key: 'word_count', label: 'Words', sortKey: 'word_count', numeric: true, width: 74, render: (r) => fmtInt(r.word_count) },
  ]

  return (
    <PageHeader
      title="Updates"
      sub={
        query.data
          ? `${fmtInt(query.data.total)} records` +
            (query.data.alumniHidden
              ? ` · ${fmtInt(query.data.alumniHidden)} from alumni records hidden`
              : '')
          : 'Loading…'
      }
      actions={
        <>
          <ViewToggle view={view} onView={setView} />
          <button className="btn btn-sm" onClick={exportAll} disabled={!query.data?.total}>
            ↓ CSV
          </button>
          {user.caps.create && (
            <Link className="btn btn-sm btn-primary" to="/newsletters/new">
              + New Update
            </Link>
          )}
        </>
      }
    >
      <div className="col">
        {/* Above the filters rather than inside them, as on the other list pages. Same `q`
            filter, so it still counts as one applied and the panel's ✕ still clears it. */}
        <SearchInput
          id="nl-q"
          value={f.q}
          onChange={(v) => set({ q: v })}
          placeholder="Body, prayer, code or name…"
          aria-label="Search updates"
        />
        <Filters id="newsletters" active={active} onClear={() => set(EMPTY)}>
          <Field label="Partner Unit" htmlFor="nl-partner">
            <PartnerSelect inputId="nl-partner" multi value={f.partner_id} onChange={(v) => set({ partner_id: v })} />
          </Field>
          {/*
            Two countries, because an update can be filed under one country while the
            partner who sent it is registered in another — the disagreement the
            Country agreement filter below reports. The labels say which is which
            rather than carrying a hint, since a hint under a control in this grid
            lifts it off the row's baseline.
          */}
          <Field label="Update's country" htmlFor="nl-country">
            <CountrySelect inputId="nl-country" multi value={f.country_id} onChange={(v) => set({ country_id: v })} />
          </Field>
          <Field label="Partner unit's country" htmlFor="nl-pcountry">
            <CountrySelect
              inputId="nl-pcountry"
              multi
              value={f.partner_country_id}
              onChange={(v) => set({ partner_country_id: v })}
            />
          </Field>
          <Field label="Date range">
            <DateRangeField from={f.from} to={f.to} onFrom={(v) => set({ from: v })} onTo={(v) => set({ to: v })} />
          </Field>
          <Field label="Year" htmlFor="nl-year">
            <YearSelect inputId="nl-year" value={f.year} onChange={(v) => set({ year: v })} />
          </Field>
          <Field label="Highlighted need" htmlFor="nl-hl">
            <EnumSelect
              inputId="nl-hl"
              value={f.highlighted}
              onChange={(v) => set({ highlighted: v })}
              options={[
                { value: '1', label: 'On' },
                { value: '0', label: 'Off' },
              ]}
            />
          </Field>
          <Field label="Prayer Requests" htmlFor="nl-pr">
            <EnumSelect
              inputId="nl-pr"
              value={f.has_prayer}
              onChange={(v) => set({ has_prayer: v })}
              options={[
                { value: '1', label: 'Present' },
                { value: '0', label: 'Missing' },
              ]}
            />
          </Field>
          <Field label="Origin" htmlFor="nl-src">
            <EnumSelect
              inputId="nl-src"
              value={f.source}
              onChange={(v) => set({ source: v })}
              options={[
                { value: 'import', label: 'Imported from CSV' },
                { value: 'manual', label: 'Added in the app' },
              ]}
            />
          </Field>
          {/* Unset means all three kinds, which is what this list has always shown and
              what it goes on showing — the registry gained two kinds of record, not two
              kinds of update. This is here so a report that used to mean "staff units,
              because that is all there was" can be made to mean it again in one click. */}
          <Field label="Written by" htmlFor="nl-fund">
            <EnumSelect
              inputId="nl-fund"
              value={f.fund_type}
              onChange={(v) => set({ fund_type: v })}
              options={[
                { value: 'staff', label: 'Partner units' },
                { value: 'training', label: 'Training schools' },
                { value: 'project', label: 'Projects' },
              ]}
            />
          </Field>
          <Field label="Country agreement" htmlFor="nl-mm">
            <EnumSelect
              inputId="nl-mm"
              value={f.country_mismatch}
              onChange={(v) => set({ country_mismatch: v })}
              options={[
                { value: '1', label: 'Differs from partner unit' },
                { value: '0', label: 'Matches partner unit' },
              ]}
            />
          </Field>

          <AlumniToggle
            checked={f.include_alumni}
            onChange={(v) => set({ include_alumni: v })}
            where="updates"
          />

          <FilterActions active={active} onClear={() => set(EMPTY)} busy={query.isFetching}>
            <DatePresets onPick={(from, to) => set({ from: from ?? '', to: to ?? '' })} />
          </FilterActions>
        </Filters>

        {view === 'table' ? (
          <PagedTable
            columns={columns}
            data={query.data}
            loading={query.isLoading}
            error={query.isError ? query.error : null}
            refetch={query.refetch}
            sort={sort}
            dir={dir}
            onSort={onSort}
            rowTo={(r) => `/newsletters/${r.id}`}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n)
              setPage(1)
            }}
          />
        ) : (
          <PagedCards
            data={query.data}
            loading={query.isLoading}
            error={query.isError ? query.error : null}
            refetch={query.refetch}
            columns={columns}
            sort={sort}
            dir={dir}
            onSort={onSort}
            card={(r) => <UpdateCard r={r} />}
            empty={<Empty icon="mail" title="No updates match these filters" />}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n)
              setPage(1)
            }}
          />
        )}
      </div>
    </PageHeader>
  )
}

/**
 * How much of an update's text the list endpoint sends: `substr(body_text, 1, 240)`.
 * Knowing it is what lets the card say the text was cut rather than let a sentence stop
 * dead — a snippet this long is one that ran out of room, not one that ended.
 */
const SNIPPET_CHARS = 240

/**
 * One update as a card: when and from whom along the top, then as much of the opening as
 * the archive keeps — 240 characters, which is a paragraph or the first few sentences.
 *
 * The table shows the same opening clamped to two lines in a column beside five others;
 * here it is the card. Clicking anywhere opens the record, the way a table row does,
 * except on the links inside — the date and the partner unit, which go where they say —
 * and except when a click ends a text selection, since somebody copying a line out is
 * not asking to navigate.
 */
function UpdateCard({ r }) {
  const navigate = useNavigate()

  const open = (e) => {
    if (e.target.closest('a,button,input,label,select')) return
    if (window.getSelection?.()?.toString()) return
    navigate(`/newsletters/${r.id}`)
  }

  return (
    <section className="card up-card clickable" onClick={open}>
      <div className="card-body">
        <div className="up-head">
          <Link to={`/newsletters/${r.id}`} className="mono">
            {r.published_on}
          </Link>
          <span className="tiny subtle">{relDays(r.published_on)}</span>
          <span className="spacer" />
          <span className="row row-tight">
            {r.highlighted_need ? <Badge tone="warn" title="Highlighted need"><Icon name="star" size={12} /></Badge> : null}
            {r.has_prayer_request ? <Badge tone="accent" title="Has prayer requests"><Icon name="prayer" size={12} /></Badge> : null}
            {r.source === 'manual' ? <Badge title="Added in the app"><Icon name="edit" size={12} /></Badge> : null}
            {r.partner_status === 'alumni' ? <Badge title="Alumni partner unit">alum</Badge> : null}
            <span className="tiny subtle nums">{fmtInt(r.word_count)}w</span>
          </span>
        </div>

        <div className="small">
          <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />
          <span className="subtle"> · </span>
          <CountryLabel id={r.country_id} name={r.country_name} />
          {r.country_mismatch ? (
            <Badge
              tone="warn"
              title={`Partner unit is registered in ${r.partner_country_name}`}
              style={{ marginLeft: 4 }}
            >
              ≠
            </Badge>
          ) : null}
        </div>

        <p className="up-text small muted">
          {r.snippet ? (
            <>
              {r.snippet}
              {r.snippet.length >= SNIPPET_CHARS ? '…' : null}
            </>
          ) : (
            <span className="subtle">No text recorded.</span>
          )}
        </p>
      </div>
    </section>
  )
}
